import { Outlet, RouteObject, createBrowserRouter } from 'react-router-dom'
import { AdminDashboardScreen } from '~/admin/admin-dashboard-screen'
import {
  RequireSystemAdminSignedIn,
  RequireUserSignedIn,
  RequireUserSignedOut,
} from '~/auth/auth-layouts'
import { LoginLayout } from '~/auth/login-layout'
import {
  credentialsLoginPath,
  dashboardPath,
  usersPath,
  emailAuthPath,
  loginPath,
  profilePath,
  profileEditPath,
  rootPath,
  playlistsPath,
  reportingPath,
  schedulesPath,
  trackPath,
  tracksPath,
  voiceMessagesPath,
  voicePlaylistRequestsPath,
  zonePath,
  zoneBillingPath,
  zonesPath,
  billingPath,
  organizationSettingsPath,
} from "~/common/paths"
import { SidebarLayout } from "~/layouts/sidebar-layout"
import { CredentialsLoginScreen } from "~/login/credentials-login-screen"
import { EmailAuthScreen } from "~/login/email-auth-screen"
import { EmailLoginScreen } from "~/login/email-login-screen"
import { ProfileScreen } from "~/profile/profile-screen"
import { ProfileEditScreen } from "~/profile/profile-edit-screen"
import { ErrorBoundary } from "../ui/error-boundary"
import { RootLayout } from "./root-layout"
import { RootScreen } from "./root-screen"
import { PlaylistsScreen } from "~/playlists-stations/playlists-screen"
import { PlaylistScreen } from "~/playlists-stations/playlist-screen"
import { SchedulesScreen } from "~/schedules/schedules-screen"
import { ScheduleScreen } from "~/schedules/schedule-screen"
import { NewScheduleScreen } from "~/schedules/new-schedule-screen"
import { EditScheduleScreen } from "~/schedules/edit-schedule-screen"
import { ZoneScreen } from "~/zones/zone-screen"
import { ZonesScreen } from "~/zones/zones-screen"
import { ZoneBillingScreen } from "~/zones/zone-billing-screen"
import { ReportingScreen } from "~/reporting/reporting-screen"
import { VoiceMessagesScreen } from "~/voice-messages/voice-messages-screen"
import { NewVoiceMessageScreen } from "~/voice-messages/new-voice-message-screen"
import { EditVoiceMessageScreen } from "~/voice-messages/edit-voice-message-screen"
import { VoiceMessageSchedulesScreen } from "~/voice-messages/voice-message-schedules-screen"
import { TracksScreen } from "~/tracks/tracks-screen"
import { VoicePlaylistRequestsScreen } from "~/voice-playlist-requests/voice-playlist-requests-screen"
import { BillingScreen } from "~/billing/billing-screen"
import { UsersScreen } from "~/users/users-screen"
import { OrganizationSettingsScreen } from "~/settings/organization-settings-screen"

// Spotify callback component
const SpotifyCallback = () => {
  // Redirect to playlists with query params preserved
  const searchParams = new URLSearchParams(window.location.search)
  window.location.href = `/playlists?${searchParams.toString()}`
  return <div>Connecting to Spotify...</div>
}

const systemAdminAuthenticatedRoutes: Array<RouteObject> = [
  {
    path: dashboardPath.pattern,
    element: <AdminDashboardScreen />,
  },
  {
    path: usersPath.pattern,
    element: <UsersScreen />,
  },
  {
    path: playlistsPath.pattern,
    element: <PlaylistsScreen />,
  },
  {
    path: playlistsPath.pattern + '/:playlistId',
    element: <PlaylistScreen />,
  },
  {
    path: schedulesPath.pattern,
    element: <SchedulesScreen />,
  },
  {
    path: schedulesPath.pattern + '/:scheduleId',
    element: <ScheduleScreen />,
  },
  {
    path: schedulesPath.pattern + '/new',
    element: <NewScheduleScreen />,
  },
  {
    path: schedulesPath.pattern + '/:scheduleId/edit',
    element: <EditScheduleScreen />,
  },
  {
    path: zonesPath.pattern,
    element: <ZonesScreen />,
  },
  {
    path: zonePath.pattern,
    element: <ZoneScreen />,
  },
  {
    path: zoneBillingPath.pattern,
    element: <ZoneBillingScreen />,
  },
  {
    path: reportingPath.pattern,
    element: <ReportingScreen />,
  },
  {
    path: voiceMessagesPath.pattern,
    element: <VoiceMessagesScreen />,
  },
  {
    path: voicePlaylistRequestsPath.pattern,
    element: <VoicePlaylistRequestsScreen />,
  },
  {
    path: voiceMessagesPath.pattern + "/new",
    element: <NewVoiceMessageScreen />,
  },
  {
    path: voiceMessagesPath.pattern + '/:voiceMessageId/edit',
    element: <EditVoiceMessageScreen />,
  },
  {
    path: voiceMessagesPath.pattern + '/:voiceMessageId/schedules',
    element: <VoiceMessageSchedulesScreen />,
  },
  {
    path: '/spotify/callback',
    element: <SpotifyCallback />,
  },
  {
    path: tracksPath.pattern,
    element: <TracksScreen />,
  },
  {
    path: trackPath.pattern,
    element: <TracksScreen />,
  },
  {
    path: billingPath.pattern,
    element: <BillingScreen />,
  },
]

const authenticatedRoutes: Array<RouteObject> = [
  {
    path: profilePath.pattern,
    element: <ProfileScreen />,
  },
  {
    path: profileEditPath.pattern,
    element: <ProfileEditScreen />,
  },
  {
    path: organizationSettingsPath.pattern,
    element: <OrganizationSettingsScreen />,
  },
  {
    path: billingPath.pattern,
    element: <BillingScreen />,
  },
]

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RequireSystemAdminSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...systemAdminAuthenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...authenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedOut />,
        children: [
          {
            element: <LoginLayout />,
            children: [
              {
                path: loginPath.pattern,
                element: <EmailLoginScreen />,
              },
              {
                path: credentialsLoginPath.pattern,
                element: <CredentialsLoginScreen />,
              },
              {
                path: emailAuthPath.pattern,
                element: <EmailAuthScreen />,
              },
            ],
          },
        ],
      },
      {
        path: rootPath.pattern,
        element: <RootScreen />,
      },
    ],
  },
])
